import { SalesChargeBreakPoint } from '@products/models/sales-charge-break-points';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
} from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { NAV } from '@models';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  PricingRangeUpdateValues,
  PricingState,
  ProductDetailConfiguration,
} from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { PricingDistributionDisplayComponent } from '../pricing-distribution-component.interface';
import { PricingDistributionConfig } from '../pricing-distribution.config';
import { PricingService } from '../services/pricing.service';
import cloneDeep from 'lodash/cloneDeep';
import { PricingHistory } from '@models';

const logger = Logger.getLogger('PricingComponent');

@Component({
  selector: 'ft-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PricingComponent
  extends BaseProductDetailComponent
  implements PricingDistributionDisplayComponent, OnDestroy {
  componentConfiguration: PricingDistributionConfig;
  dataSubscription: Subscription[] = [];
  pricingState: PricingState;
  navData: NAV;
  salesChargeBreakPointsData: SalesChargeBreakPoint[];
  filteredHistoricalList: PricingHistory[];
  historicalTableTotalPages: {
    count: number; // intentionally done, as onChanges in child didn't detected number change, when same value, even on using cloneDeep().
  };
  showSharePrices: boolean;
  showPricingBreakpoints: boolean;
  showNavHighLow: boolean;

  constructor(
    private pricingService: PricingService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  populate(
    componentConfig: PricingDistributionConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.componentConfiguration = componentConfig;
    this.changeDetector.detectChanges();
    if (this.brConfig.fundId) {
      this.pricingService.setConfigurationName(
        componentConfig.configurationName
      );
      this.dataSubscription.push(
        this.pricingService.pricingState$.subscribe((newState: PricingState) =>
          this.mapState(newState)
        )
      );
      this.dataSubscription.push(
        this.pricingService
          .getCurrentPage$()
          .subscribe(
            (historicalList) =>
              (this.filteredHistoricalList = cloneDeep(historicalList))
          )
      );
      this.dataSubscription.push(
        this.pricingService.getNumberOfPages$().subscribe((totalPages) => {
          this.historicalTableTotalPages = cloneDeep({
            count: totalPages,
          });
        })
      );
      this.pricingService.populate(brConfig);
    }
    this.showSharePrices = this.pricingService.showSharePrices();
    this.showPricingBreakpoints = this.pricingService.showPricingBreakpoints();
  }

  /**
   * Called when state changes.
   * @param pricingState the new state returned by pricing service
   */
  mapState(pricingState: PricingState): void {
    this.pricingState = cloneDeep(pricingState);
    this.navData = pricingState.overviewData.nav;
    this.salesChargeBreakPointsData =
      pricingState.overviewData.salesChargeBreakPoints;
    this.showNavHighLow = this.pricingService.showNavHighLow(pricingState);
    this.changeDetector.detectChanges();
  }

  /**
   * Call pricing service to change price range for chart.
   * This will then update state & price chart, when service provides update.
   */
  updatePriceChart(
    pricingChartRangeUpdateValues: PricingRangeUpdateValues
  ): void {
    this.pricingService.createPriceChart(
      pricingChartRangeUpdateValues.selectedToggleRange,
      pricingChartRangeUpdateValues.fromDate,
      pricingChartRangeUpdateValues.toDate
    );
  }

  /**
   * Call pricing service to change price range for table.
   * This will then update state & price table, when service provides update.
   */
  updatePriceTable(
    pricingChartRangeUpdateValues: PricingRangeUpdateValues
  ): void {
    this.pricingService.createPriceTable(
      pricingChartRangeUpdateValues.selectedToggleRange,
      pricingChartRangeUpdateValues.fromDate,
      pricingChartRangeUpdateValues.toDate
    );
  }

  /**
   * Trigger page change with the page number selected.
   * @param pageNumber page number selected
   */
  onPageChanged(pageNumber: number): void {
    this.pricingService.goToPage(pageNumber);
  }

  /**
   * Trigger download action.
   */
  onDownloadButtonClicked(): void {
    this.pricingService.triggerDownloadAction();
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.forEach((subscription) =>
      subscription.unsubscribe()
    );
  }
}
